/* GrowthOS — Ticket record (view) page.
   Rebuilt to the shared `.gos-leadrec` / `gos-lr-*` detail shell (mirrors the other
   detail pages): back row, header card (subject + status/priority badges + owner
   meta, Edit + dots), a horizontal tab bar, a read-only Overview (info cards) and
   the real Activity(Timeline)/Notes/Attachments child tabs. Replaces the legacy
   left-nav scroll-spy that reused TaskForm in "view mode" — TaskForm is now a plain
   editable form. Fetches (unchanged): fetchDetail/fetchNotes/fetchAttachments for
   module 'Ticket'. */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAttachmentsAndAppendToModule,
  fetchDetailAndAppendToModule,
  fetchNotesAndAppendToModule,
} from '../../redux/actions';
import { getTicketById } from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';
import Notes from '../sharedChild/note/notes';
import Attachments from '../sharedChild/attachment/attachments';
import Timeline from '../sharedChild/timeline';
import { Ic } from "../../shared/component/GosIcon";


const val = (v) => (v === 0 || v ? v : null);
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};
const fmtDT = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const statusTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('cancel') || s.includes('reject')) return 'red';
  if (s.includes('resolv') || s.includes('complet') || s.includes('done')) return 'green';
  if (s.includes('closed')) return 'gray';
  if (s.includes('progress') || s.includes('pending') || s.includes('hold')) return 'amber';
  if (s.includes('open') || s.includes('new') || s.includes('assign')) return 'blue';
  return 'gray';
};
const priorityTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('urgent') || s.includes('critical') || s.includes('high')) return 'red';
  if (s.includes('medium') || s.includes('normal')) return 'amber';
  if (s.includes('low')) return 'green';
  return 'gray';
};

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);

const TextCard = ({ title, icon, text }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className={'gos-lr-textbody' + (text ? '' : ' muted')}>{text || 'No content provided.'}</div>
  </div>
);

const ViewTasks = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const ticketID = searchParams.get('id');

  const [ticketLocalData, setTicketLocalData] = useState({ ...(location.state || {}), type: 'view', from: 'view' });
  const ticketStoreData = useSelector((state) => getTicketById(state, location.state?.id || ticketID));

  useEffect(() => {
    setTicketLocalData((prev) => ({ ...prev, ...ticketStoreData }));
  }, [ticketStoreData]);

  useEffect(() => {
    const ticketId = location.state?.id || ticketID;
    if (ticketId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'Ticket', id: ticketId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'Ticket', id: ticketId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'Ticket', id: ticketId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, ticketLocalData?.id]);

  const cd = ticketLocalData || {};
  const owner = NameFormatter ? NameFormatter(cd.owner) : cd.owner;

  // resolve the linked "for" record + "associated with" record for display
  const forKind = cd.leadId ? 'Lead' : cd.contactId ? 'Contact' : null;
  const forName = cd.leadId ? cd.leadName : cd.contactId ? cd.contactName : null;
  const assocKind = cd.accountId ? 'Account' : cd.opportunityId ? 'Opportunity' : cd.campaignId ? 'Campaign' : null;
  const assocName = cd.accountId ? cd.accountName : cd.opportunityId ? cd.opportunityName : cd.campaignId ? cd.campaignName : null;

  const handleOpenEditForm = () => {
    navigate('/tickets/editTicket', { state: { ...cd, type: 'edit', from: 'view' } });
  };

  const chips = [
    cd.status && { icon: 'grid', label: cd.status, tone: statusTone(cd.status) },
    cd.priority && { icon: 'flag', label: cd.priority, tone: priorityTone(cd.priority) },
    owner && { icon: 'user', label: 'Owner ' + owner },
    fmtDate(cd.dueDate) && { icon: 'calendar', label: 'Due ' + fmtDate(cd.dueDate) },
    cd.createdDateTime && { icon: 'clock', label: 'Created ' + fmtDT(cd.createdDateTime) },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'activity', label: 'Activity', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: cd.notesList?.length },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: cd.attachments?.length },
  ];

  const overview = (
    <>
      <div className="gos-lr-grid2">
        <InfoCard
          title="Ticket Information"
          icon="ticket"
          rows={[
            ['Ticket Owner', val(owner)],
            ['Subject', val(cd.subject)],
            ['Status', cd.status ? <span className={'gos-badge gos-b-' + statusTone(cd.status)}><span className="gos-pip" />{cd.status}</span> : null],
            ['Priority', cd.priority ? <span className={'gos-badge gos-b-' + priorityTone(cd.priority)}><span className="gos-pip" />{cd.priority}</span> : null],
            ['Due Date', val(fmtDate(cd.dueDate))],
            ['Reminder', val(fmtDT(cd.reminder))],
          ]}
        />
        <InfoCard
          title="Association"
          icon="link"
          rows={[
            ['Ticket For', forKind ? `${forKind}: ${forName || '—'}` : null],
            ['Associated With', assocKind ? `${assocKind}: ${assocName || '—'}` : null],
            ['Created By', val(NameFormatter ? NameFormatter(cd.createdBy) : cd.createdBy)],
            ['Created Date', val(fmtDT(cd.createdDateTime))],
          ]}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <TextCard title="Description" icon="note" text={cd.description} />
      </div>
    </>
  );

  let tabBody = overview;
  if (activeTab === 'activity') tabBody = <Timeline localData={cd} module={'Ticket'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={cd} module={'Ticket'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={cd} module={'Ticket'} />;

  return (
    <div className="ViewLeadPageOuter gos-leadrec">
      {/* back row */}
      <div className="gos-lr-backrow">
        <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/tickets')}>
          <Ic name="chevL" size={15} /> Tickets
        </button>
      </div>

      {/* header card */}
      <div className="gos-lr-card gos-lr-header">
        <div className="gos-lr-header-top">
          <div className="gos-lr-header-l">
            <span className="gos-lr-ava" style={{ background: 'var(--gos-accent-soft)', color: 'var(--gos-accent)' }}>
              <Ic name="ticket" size={22} />
            </span>
            <div className="gos-lr-header-meta">
              <div className="gos-lr-name" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {cd.subject || 'Untitled ticket'}
                {cd.status ? (
                  <span className={'gos-badge gos-b-' + statusTone(cd.status)}>
                    <span className="gos-pip" />
                    {cd.status}
                  </span>
                ) : null}
              </div>
              <div className="gos-lr-subline">
                <Ic name="user" size={14} />
                {owner || '—'}
                {forKind && forName ? (
                  <>
                    <span className="gos-lr-dot">·</span>
                    <Ic name="link" size={14} />
                    {forKind}: {forName}
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <div className="gos-lr-actions">
            <button type="button" className="gos-lr-contact-btn" onClick={handleOpenEditForm}>
              <Ic name="edit" size={16} /> Edit
            </button>
            <button
              type="button"
              className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="More actions"
            >
              <Ic name="dots" size={18} />
            </button>
            {menuOpen ? (
              <>
                <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                <div className="gos-lr-menu">
                  <button type="button" className="gos-lr-menu-item" onClick={() => { setMenuOpen(false); handleOpenEditForm(); }}>
                    <Ic name="edit" size={15} /> Edit Ticket
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
        {chips.length ? (
          <div className="gos-lr-chips">
            {chips.map((c, i) => (
              <span className={'gos-lr-chip' + (c.tone ? ' gos-lr-chip-' + c.tone : '')} key={i}>
                <Ic name={c.icon} size={14} /> {c.label}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {/* tabs */}
      <div className="gos-lr-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
            onClick={() => setActiveTab(t.id)}
          >
            <Ic name={t.icon} size={15} /> {t.label}
            {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
          </button>
        ))}
      </div>

      {/* body */}
      {tabBody}
    </div>
  );
};

export default ViewTasks;
